import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { billingApi } from '../api/billing.api';
import { getAppBridge } from '../api/client';
import { redirectToUrl } from '../utils/redirect';
import { toErrorMessage } from '../utils/errorMessage';
import type { ShopPlanInfo } from '../types/billing.types';

interface BillingContextType {
  plan: ShopPlanInfo | null;
  trial: {
    active: boolean;
    used: boolean;
    expired: boolean;
    expiresAt: string | null;
  } | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createCheckout: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export function BillingProvider({ children }: { children: ReactNode }) {
  const [planInfo, setPlanInfo] = useState<ShopPlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPlan = useCallback(async () => {
    try {
      // Don't set loading to true on refetch to avoid UI flicker
      if (!planInfo) setLoading(true);
      setError(null);
      const data = await billingApi.getPlan();
      setPlanInfo(data);
      // NOTE: keys in settings responses are SHA-256 digests at rest, so
      // auto-syncing them into localStorage can never authenticate. Dev
      // admin keys must be entered manually.
    } catch (err) {
      setError(new Error(toErrorMessage(err, 'Failed to fetch billing info')));
    } finally {
      setLoading(false);
    }
  }, [planInfo]);

  // Initial fetch, with a bounded retry. The first mount can race the App
  // Bridge session-token provider (a 401 with no token), so a single failed
  // attempt must not permanently strand the UI on "Unable to load plan
  // information". Retry a few times with backoff until a plan loads.
  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 4;

    const run = async () => {
      try {
        if (attempt === 0 && !planInfo) setLoading(true);
        const data = await billingApi.getPlan();
        if (cancelled) return;
        setError(null);
        setPlanInfo(data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        attempt += 1;
        if (attempt < maxAttempts) {
          // 400ms, 800ms, 1600ms — enough for App Bridge to mint a token.
          setTimeout(run, 400 * 2 ** (attempt - 1));
        } else {
          setError(new Error(toErrorMessage(err, 'Failed to fetch billing info')));
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCheckout = useCallback(async () => {
    try {
      const { checkout_url } = await billingApi.createCheckoutSession();
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      // MUST redirect the TOP frame via App Bridge — a bare window.location.href
      // is blocked by the Admin iframe CSP, so "Subscribe" would do nothing.
      redirectToUrl(checkout_url, getAppBridge());
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create checkout session');
    }
  }, []);

  // Prefer the server-computed trial state (immune to client clock skew);
  // fall back to deriving it from the flat fields for older backend responses.
  const trial = planInfo
    ? planInfo.trial
      ? {
          active: planInfo.trial.active,
          used: planInfo.trial.used,
          expired: planInfo.trial.expired,
          expiresAt: planInfo.trial.expires_at,
        }
      : {
          active: !planInfo.free_trial_used && planInfo.free_trial_expires_at
            ? new Date(planInfo.free_trial_expires_at) > new Date()
            : false,
          used: planInfo.free_trial_used,
          expired: planInfo.free_trial_expires_at
            ? new Date(planInfo.free_trial_expires_at) <= new Date()
            : false,
          expiresAt: planInfo.free_trial_expires_at,
        }
    : null;

  return (
    <BillingContext.Provider
      value={{
        plan: planInfo,
        trial,
        loading,
        error,
        refetch: fetchPlan,
        createCheckout,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}

